import React, { useEffect, useMemo, useState } from 'react';
import menuGrants from '../../utils/menuGrantTree';

const {
  coveredByStoredGrant,
  directlyGranted,
  toggleMenuGrant,
  branchMatches,
  indexMenu,
} = menuGrants;

function MenuGrantTree({ structure, grants, onChange, query, disabled }) {
  const index = useMemo(() => indexMenu(structure), [structure]);
  const [expanded, setExpanded] = useState({});
  const q = String(query || '').trim();

  useEffect(() => {
    if (!q) return;
    const next = {};
    const openAncestors = (id) => {
      let parent = index.parentOf.get(id);
      while (parent) {
        next[parent] = true;
        parent = index.parentOf.get(parent);
      }
    };
    for (const [id, node] of index.byId) {
      if (menuGrants.nodeMatchesQuery(node, q)) openAncestors(id);
    }
    setExpanded((prev) => ({ ...prev, ...next }));
  }, [q, index]);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderItem = (item, level) => {
    if (q && !branchMatches(item, q, index)) return null;
    const kids = item.subItems || [];
    const hasKids = kids.length > 0;
    const open = !!expanded[item.id];
    const locked = coveredByStoredGrant(item.id, grants, index);
    const checked = locked || directlyGranted(item.id, grants);
    const partial = !checked && (function someDescendant() {
      const stack = [...(index.childrenOf.get(item.id) || [])];
      const set = new Set(grants);
      while (stack.length) {
        const id = stack.pop();
        if (set.has(id)) return true;
        stack.push(...(index.childrenOf.get(id) || []));
      }
      return false;
    }());

    return (
      <div key={item.id}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '3px 0 3px ' + (level * 16) + 'px',
          borderBottom: '1px solid #f3f4f6',
        }}>
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled || locked}
            ref={(input) => {
              if (input) input.indeterminate = partial && !locked;
            }}
            onChange={() => {
              if (disabled || locked) return;
              onChange(toggleMenuGrant(grants, item.id, index));
            }}
            title={locked ? 'Included because a parent menu is granted' : item.id}
            style={{ width: '15px', height: '15px', margin: 0, cursor: locked ? 'not-allowed' : 'pointer' }}
          />
          {hasKids ? (
            <button
              type="button"
              onClick={() => toggleExpand(item.id)}
              aria-label={open ? 'Collapse ' + item.label : 'Expand ' + item.label}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                width: '16px',
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '10px',
              }}
            >
              {open ? '▼' : '▶'}
            </button>
          ) : (
            <span style={{ width: '16px' }} />
          )}
          <span
            onClick={() => { if (hasKids) toggleExpand(item.id); }}
            style={{
              flex: 1,
              fontSize: '13px',
              fontWeight: level === 0 ? 600 : 400,
              cursor: hasKids ? 'pointer' : 'default',
              color: locked ? '#6b7280' : '#111827',
            }}
          >
            {item.label}
            {locked && (
              <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 400, color: '#9ca3af' }}>
                included with parent
              </span>
            )}
            {item.requiresProject && (
              <span style={tagStyle('#0e7490', '#ecfeff')}>Project</span>
            )}
            {item.requiresEstimate && (
              <span style={tagStyle('#6d28d9', '#f5f3ff')}>Estimate</span>
            )}
            {item.hideInProject && (
              <span style={tagStyle('#4b5563', '#f3f4f6')}>Global</span>
            )}
          </span>
        </div>
        {hasKids && open && kids.map((sub) => renderItem(sub, level + 1))}
      </div>
    );
  };

  const visible = (structure || []).map((item) => renderItem(item, 0)).filter(Boolean);
  if (q && visible.length === 0) {
    return <div style={{ fontSize: '13px', color: '#6b7280', padding: '8px 0' }}>No menus match.</div>;
  }
  return <div>{visible}</div>;
}

function tagStyle(color, background) {
  return {
    marginLeft: '6px',
    fontSize: '10px',
    fontWeight: 500,
    color,
    background,
    borderRadius: '3px',
    padding: '1px 4px',
  };
}

export default MenuGrantTree;
